import { Alert, Box, Chip, CircularProgress, Link, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import { TREASURY_KEY, treasuryApi } from '../../api/treasury.api';

import { ConnectionsLink } from './ConnectionsLink';
import { SettingsSectionHeader } from './SettingsSectionHeader';
import { TreasuryForm } from './TreasuryForm';
import { COMMISSION_TRANSFER_MODE_KEY } from './commission-transfer-mode';
import { useCanWriteSection } from './useSettingsAccess';

/**
 * Settings -> Treasury -> Commission transfer (DEV-132, DEV-133, DEV-139).
 *
 * The DEV-127 settings that decide where Norvix commissions go and when they
 * are moved: the `TREASURY_COMMISSION` fields of GuruPay's Treasury settings,
 * stored as versions of `treasury.gurupay` (DEV-139), apart from the GuruPay
 * connection. A new revenue account or switching to LIVE asks for a reason,
 * which the API requires; the History reads the versions.
 */

const MODE_MEANING: Readonly<Record<string, string>> = {
  OFF: 'Nothing runs.',
  CALCULATE_ONLY: 'Every day the amount is worked out and recorded on the Transfers tab. No money moves.',
  LIVE: 'Every day the amount is moved from the client-money account to the revenue account.',
};

export function TreasuryCommissionTransferPage(): JSX.Element {
  const treasury = useQuery({ queryKey: TREASURY_KEY, queryFn: () => treasuryApi.list() });
  const gurupay = (treasury.data ?? []).find((p) => p.provider === 'GURUPAY');
  // DEV-140: MLRO, Client manager and Finance read Treasury; Admin changes it.
  const readOnly = !useCanWriteSection({ group: 'treasury', id: 'commission-transfer' });
  const storedMode = String(gurupay?.fields.find((f) => f.key === COMMISSION_TRANSFER_MODE_KEY)?.value ?? '') || 'OFF';

  return (
    <Box>
      <SettingsSectionHeader
        title="Commission transfer"
        lastChanged={gurupay === undefined ? undefined : { at: gurupay.updatedAt, by: gurupay.updatedBy, of: 'GuruPay Treasury settings' }}
      >
        Norvix commissions are charged on the client-money account, where customer money is held. This moves
        them every day to Norvix&apos;s own revenue account, so the client-money account holds only what
        customers are owed. A new revenue account or switching to LIVE needs a reason. The first LIVE run for a
        currency is recorded as the opening transfer, and no run ever moves more than Norvix&apos;s own money.
      </SettingsSectionHeader>
      {treasury.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : treasury.isError || gurupay === undefined ? (
        <Alert severity="error">Could not load the treasury settings.</Alert>
      ) : (
        <>
          {!gurupay.connection.configured ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              The GuruPay connection is incomplete (still needed: {gurupay.connection.missingFields.join(', ')}). Complete it in{' '}
              <ConnectionsLink /> before choosing a revenue account.
            </Alert>
          ) : null}
          <TreasuryForm
            provider={gurupay}
            section="TREASURY_COMMISSION"
            historyTitle="Commission transfer"
            readOnly={readOnly}
            badge={<Chip size="small" variant="outlined" label={`Mode: ${storedMode}`} color={storedMode === 'LIVE' ? 'warning' : 'default'} />}
            // A master (client-money) account holds customer money, so it is never offered as the revenue account.
            discovery={{ fill: false, offer: (accounts, field) => (field.key === 'revenueAccountUuids' ? accounts.filter((a) => !a.isMaster) : []) }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {MODE_MEANING[storedMode] ?? ''} Runs and their results are on{' '}
              <Link component={RouterLink} to="/commissions?tab=transfers">Fees &amp; Commissions → Transfers</Link>.
            </Typography>
          </TreasuryForm>
        </>
      )}
    </Box>
  );
}
